import { Pill } from "@/components/ui/pill";
import type { Adherence, AdherenceDimensionKey } from "@/types/applied-scenario.types";
import {
  DIMENSION_HINTS,
  DIMENSION_LABELS,
  VERDICT_HINTS,
  VERDICT_LABELS,
  VERDICT_TONES,
} from "./applied-scenario-labels";

interface AdherencePanelProps {
  adherence: Adherence | null;
  /** O limiar DECLARADO no ato de aplicar — o número contra o qual o índice é lido. */
  thresholdPct: number;
}

// A ADERÊNCIA ao compromisso: quatro dimensões e a pior delas.
// ⚠️ Dimensão NÃO MEDIDA renderiza "—", JAMAIS `0`. Uma dimensão sem dado e uma que
// desviou por completo levam a ações opostas: a primeira pede instrumentação, a segunda pede
// conversa com a operação. Mostrar `0` para as duas apagaria essa diferença na única superfície em
// que ela importa — a tela onde alguém decide o que fazer.
// O índice é o MÍNIMO, e a dimensão responsável é NOMEADA: um número sozinho diz que algo saiu do
// combinado sem dizer o quê, e quem lê fica com a pergunta em vez da ação.

const DIMENSION_ORDER: readonly AdherenceDimensionKey[] = ["sequence", "mix", "shifts", "machines"];

/** `—` é o valor de "não medida". Nunca `0`, nunca `0%`. */
function formatPct(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(1).replace(".", ",")}%`;
}

export function AdherencePanel({ adherence, thresholdPct }: AdherencePanelProps) {
  if (!adherence) {
    return (
      <div className="rounded-md border border-border-subtle p-3">
        <p className="text-[13px] font-medium text-text-primary">Aderência ao compromisso</p>
        <p className="mt-1 text-[12px] text-text-secondary">
          Ainda não avaliada — a vigência deste compromisso não começou.
        </p>
      </div>
    );
  }

  return (
    <section className="space-y-3 rounded-md border border-border-subtle p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-text-primary">
            Aderência ao compromisso
          </span>
          {/* Status com cor + dot + rótulo — nunca só por cor. */}
          <Pill tone={VERDICT_TONES[adherence.verdict]} withDot>
            {VERDICT_LABELS[adherence.verdict]}
          </Pill>
        </div>
        <span className="text-[13px] tabular-nums text-text-secondary">
          Índice{" "}
          <span className="text-[15px] font-medium text-text-primary">
            {formatPct(adherence.index)}
          </span>{" "}
          · limiar {thresholdPct.toFixed(0)}%
        </span>
      </div>

      <p className="text-[12px] text-text-secondary">{VERDICT_HINTS[adherence.verdict]}</p>

      <dl className="grid gap-2 sm:grid-cols-2">
        {DIMENSION_ORDER.map((key) => {
          const value = adherence.dimensions[key] ?? null;
          const isDriver = adherence.driverDimension === key;
          return (
            <div
              key={key}
              className="flex items-start justify-between gap-3 rounded-md border border-border-subtle px-2.5 py-2"
            >
              <div className="min-w-0">
                <dt className="flex items-center gap-1.5 text-[12px] font-medium text-text-primary">
                  {DIMENSION_LABELS[key]}
                  {/* A responsável pelo índice é nomeada, não só implícita no menor número. */}
                  {isDriver ? <Pill tone="neutral">responsável pelo índice</Pill> : null}
                </dt>
                <p className="mt-0.5 text-[11px] text-text-secondary">{DIMENSION_HINTS[key]}</p>
              </div>
              <dd className="shrink-0 text-[13px] tabular-nums text-text-primary">
                {formatPct(value)}
              </dd>
            </div>
          );
        })}
      </dl>

      <p className="text-[11px] text-text-tertiary">
        {adherence.frozen
          ? "Valor congelado na apuração — não muda mais."
          : "Avaliada continuamente enquanto a vigência corre; o valor muda no próximo ciclo."}
      </p>
    </section>
  );
}
